import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TeslaRobot from '../components/TeslaRobot';

const PricingPage: React.FC = () => {
  const [seats, setSeats] = useState<number>(5);
  const [agents, setAgents] = useState<number>(3);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  
  const basePrice = 20; // $20 per seat per month
  const annualDiscount = 0.2; // 20% discount for annual billing
  
  const calculatePrice = () => {
    let price = basePrice * seats * agents;
    
    if (billingCycle === 'annual') {
      price = price * 12 * (1 - annualDiscount);
    }
    
    return price;
  };
  
  const formatPrice = (price: number) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };
  
  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <div className="pricing-title-container">
          <h1 className="pricing-title">
            Flexible <span className="text-gradient">Pricing</span>
          </h1>
          <p className="pricing-subtitle">
            Choose the plan that works best for your organization
          </p>
        </div>
        <div className="pricing-robot">
          <TeslaRobot width={120} height={120} />
        </div>
      </div>
      
      <div className="pricing-plans">
        <div className="pricing-plan free">
          <div className="plan-header">
            <h2>Free Tier</h2>
            <div className="plan-price">$0</div>
            <div className="plan-billing">forever</div>
          </div>
          <div className="plan-features">
            <ul>
              <li>1 user seat</li>
              <li>1 AI employee</li>
              <li>5 hours of usage per month</li>
              <li>Basic skills only</li>
              <li>Email support</li>
            </ul>
          </div>
          <div className="plan-cta">
            <Link to="/employees" className="plan-button free">
              Get Started
            </Link>
          </div>
        </div>
        
        <div className="pricing-plan starter">
          <div className="plan-header">
            <h2>Starter</h2>
            <div className="plan-price">$20</div>
            <div className="plan-billing">per seat per month</div>
          </div>
          <div className="plan-features">
            <ul>
              <li>Up to 10 user seats</li>
              <li>Up to 5 AI employees</li>
              <li>Unlimited usage</li>
              <li>All skills included</li>
              <li>Priority email support</li>
              <li>Basic analytics</li>
            </ul>
          </div>
          <div className="plan-cta">
            <Link to="/employees" className="plan-button starter">
              Choose Starter
            </Link>
          </div>
        </div>
        
        <div className="pricing-plan business">
          <div className="plan-badge">Most Popular</div>
          <div className="plan-header">
            <h2>Business</h2>
            <div className="plan-price">$20</div>
            <div className="plan-billing">per seat per month</div>
          </div>
          <div className="plan-features">
            <ul>
              <li>Unlimited user seats</li>
              <li>Unlimited AI employees</li>
              <li>Unlimited usage</li>
              <li>All skills included</li>
              <li>Priority phone & email support</li>
              <li>Advanced analytics</li>
              <li>API access</li>
              <li>Custom integrations</li>
            </ul>
          </div>
          <div className="plan-cta">
            <Link to="/employees" className="plan-button business">
              Choose Business
            </Link>
          </div>
        </div>
        
        <div className="pricing-plan enterprise">
          <div className="plan-header">
            <h2>Enterprise</h2>
            <div className="plan-price">Custom</div>
            <div className="plan-billing">tailored to your needs</div>
          </div>
          <div className="plan-features">
            <ul>
              <li>Everything in Business plan</li>
              <li>Custom AI employee development</li>
              <li>Dedicated account manager</li>
              <li>Service level agreement (SLA)</li>
              <li>On-premise deployment options</li>
              <li>Custom security requirements</li>
              <li>24/7 premium support</li>
            </ul>
          </div>
          <div className="plan-cta">
            <Link to="/contact" className="plan-button enterprise">
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
      
      <div className="pricing-calculator">
        <h2>Calculate Your Price</h2>
        <p>Estimate your monthly cost based on your organization's needs</p>
        
        <div className="calculator-controls">
          <div className="calculator-row">
            <div className="calculator-label">
              <label htmlFor="seats">Number of User Seats</label>
              <span className="calculator-value">{seats} seats</span>
            </div>
            <input
              type="range"
              id="seats"
              min="1"
              max="50"
              value={seats}
              onChange={(e) => setSeats(parseInt(e.target.value))}
              className="calculator-slider"
            />
          </div>
          
          <div className="calculator-row">
            <div className="calculator-label">
              <label htmlFor="agents">Number of AI Employees</label>
              <span className="calculator-value">{agents} agents</span>
            </div>
            <input
              type="range"
              id="agents"
              min="1"
              max="20"
              value={agents}
              onChange={(e) => setAgents(parseInt(e.target.value))}
              className="calculator-slider"
            />
          </div>
          
          <div className="calculator-row billing-toggle">
            <div className="calculator-label">
              <label>Billing Cycle</label>
            </div>
            <div className="billing-options">
              <button
                className={`billing-option ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                Monthly
              </button>
              <button
                className={`billing-option ${billingCycle === 'annual' ? 'active' : ''}`}
                onClick={() => setBillingCycle('annual')}
              >
                Annual (20% off)
              </button>
            </div>
          </div>
        </div>
        
        <div className="calculator-result">
          <div className="result-label">Estimated {billingCycle === 'annual' ? 'Annual' : 'Monthly'} Cost:</div>
          <div className="result-price">{formatPrice(calculatePrice())}</div>
          <div className="result-details">
            {seats} seats × {agents} AI employees × ${basePrice}/seat/month
            {billingCycle === 'annual' ? ' × 12 months with 20% discount' : ''}
          </div>
        </div>
        
        <div className="calculator-cta">
          <Link to="/contact" className="calculator-button">
            Contact Sales for Custom Quote
          </Link>
        </div>
      </div>
      
      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>
        
        <div className="faq-grid">
          <div className="faq-item">
            <h3>What is a "seat"?</h3>
            <p>
              A seat represents one user in your organization who can access and use the Digital Workplace platform. 
              Each seat can interact with any of your AI employees.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>What is an "AI employee"?</h3>
            <p>
              An AI employee is a digital assistant with specific skills that can perform tasks for your organization. 
              Different AI employees have different specializations, such as HR, development, or project management.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Can I change my plan later?</h3>
            <p>
              Yes, you can upgrade, downgrade, or cancel your plan at any time. Changes to your plan will be reflected 
              in your next billing cycle.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Is there a limit to how much I can use the AI employees?</h3>
            <p>
              The Free tier has a limit of 5 hours per month. All paid plans include unlimited usage of your AI employees.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Do you offer discounts for non-profits or educational institutions?</h3>
            <p>
              Yes, we offer special pricing for non-profits, educational institutions, and startups. Please contact our 
              sales team for more information.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>What payment methods do you accept?</h3>
            <p>
              We accept all major credit cards, PayPal, and bank transfers for annual plans. Enterprise customers can also 
              be invoiced according to their company policies.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
